import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from '../common/dialogStyles';
import {
  ProgramApiService,
  ExerciseState,
} from '../../services/api/ProgramApiService';

interface EditWeightsDialogProps {
  open: boolean;
  onClose: () => void;
  enrollmentId: number;
  exerciseStates: ExerciseState[];
  onWeightsUpdated: () => void;
}

export const EditWeightsDialog: React.FC<EditWeightsDialogProps> = ({
  open,
  onClose,
  enrollmentId,
  exerciseStates,
  onWeightsUpdated,
}) => {
  const { t } = useLanguage();

  const [weights, setWeights] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      exerciseStates.map((s) => [s.program_exercise_id, String(s.current_weight_kg)])
    )
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (programExerciseId: number, value: string) => {
    setWeights((prev) => ({ ...prev, [programExerciseId]: value }));
  };

  const handleSubmit = async () => {
    // Validate all weights are positive numbers
    const parsed: { program_exercise_id: number; weight_kg: number }[] = [];
    for (const [idStr, valStr] of Object.entries(weights)) {
      const val = parseFloat(valStr);
      if (isNaN(val) || val <= 0) {
        setError(`Invalid weight for exercise ${idStr}`);
        return;
      }
      parsed.push({ program_exercise_id: Number(idStr), weight_kg: val });
    }

    setSubmitting(true);
    setError(null);
    try {
      await ProgramApiService.updateEnrollmentWeights(enrollmentId, parsed);
      onWeightsUpdated();
      onClose();
    } catch (err: any) {
      setError(err.message ?? 'Failed to update weights');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={commonDialogStyles}>
      <DialogTitle>{t('programs.editWeights')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('programs.cannotEditWeights')}
        </Typography>

        {exerciseStates.map((state) => (
          <Box key={state.program_exercise_id} sx={{ mb: 2 }}>
            <TextField
              label={state.program_exercise_name}
              type="number"
              size="small"
              fullWidth
              value={weights[state.program_exercise_id] ?? ''}
              onChange={(e) => handleChange(state.program_exercise_id, e.target.value)}
              slotProps={{
                input: { inputProps: { min: 0, step: 2.5 } },
              }}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel') || 'Cancel'}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={20} /> : t('common.save') || 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
